/**
 * ConfirmDialog — small modal for destructive / one-way actions.
 *
 * Replaces ad-hoc `window.confirm()` calls so the UX matches the
 * rest of the app (glass panel, ESC dismisses, focus trap-able). Use
 * sparingly — most flows should be undoable instead of guarded.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { useFocusTrap } from '../lib/useFocusTrap';

interface Props {
  title: string;
  body?: string;
  /** Defaults to 'Confirm'. */
  confirmLabel?: string;
  /** Defaults to 'Cancel'. */
  cancelLabel?: string;
  /** Tinting for destructive actions. */
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  onConfirm,
  onCancel,
}: Props) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  // Auto-focus the confirm button so Enter is the natural primary action.
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  // ESC dismisses.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const dialog = (
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-menu-portal="true"
    >
      <div
        ref={containerRef}
        className="account-modal-wrap"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        tabIndex={-1}
        style={{ width: 'min(440px, 100%)' }}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <header
            className="account-modal__head"
            style={{ alignItems: 'center', borderBottom: 'none', paddingBottom: 4 }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: danger
                  ? 'linear-gradient(135deg, rgba(226,57,57,0.24), rgba(253,173,1,0.14))'
                  : 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
                border: '1px solid rgba(255,255,255,0.16)',
                color: danger ? '#ffd06b' : 'rgba(232,121,217,0.9)',
                marginRight: 10,
              }}
              aria-hidden="true"
            >
              <AlertTriangle size={18} strokeWidth={2} />
            </div>
            <h2 id="confirm-dialog-title" className="t-section-title" style={{ margin: 0, flex: 1 }}>
              {title}
            </h2>
            <button
              type="button"
              className="glass-btn"
              onClick={onCancel}
              aria-label="Close"
            >
              <X size={13} />
            </button>
          </header>
          {body && (
            <p className="t-body" style={{ margin: '8px 0 14px', color: 'rgba(220,226,240,0.78)' }}>
              {body}
            </p>
          )}
          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              paddingTop: 6,
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <button type="button" className="glass-btn" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button
              ref={confirmRef}
              type="button"
              className={`glass-btn ${danger ? '' : 'glass-btn--primary'}`}
              onClick={() => onConfirm()}
              style={
                danger
                  ? {
                      background: 'linear-gradient(180deg, rgba(226,57,57,0.32), rgba(187,65,152,0.18))',
                      borderColor: 'rgba(226,57,57,0.44)',
                      color: '#fff',
                      boxShadow: '0 6px 18px rgba(226,57,57,0.22)',
                    }
                  : undefined
              }
            >
              {confirmLabel}
            </button>
          </footer>
        </GlassPanel>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
